import { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  LogOut,
  Sun,
  Moon,
  Settings,
} from "lucide-react";
import { cn } from "@mira/ui";
import { useAuthStore } from "@/stores/auth";
import { useThemeStore } from "@/stores/theme";
import { useAgentSelectionStore } from "@/stores/agent-selection";
import { navItems } from "@/lib/nav-items";
import { motion, AnimatePresence } from "framer-motion";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { user, signOut } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const agents = useAgentSelectionStore((s) => s.agents);
  const selectedAgentId = useAgentSelectionStore((s) => s.selectedAgentId);
  const setSelectedAgentId = useAgentSelectionStore((s) => s.setSelectedAgentId);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      className={cn(
        "h-full bg-pure-white dark:bg-dark-card border-r border-stone dark:border-dark-border flex flex-col transition-all duration-300 ease-in-out relative z-50",
        collapsed ? "w-[72px]" : "w-[260px]",
      )}
    >
      {/* Floating Toggle on Right Border */}
      <button
        onClick={onToggle}
        className={cn(
          "absolute -right-3.5 top-7 w-7 h-7 rounded-full bg-pure-white dark:bg-dark-surface border border-stone dark:border-dark-border flex items-center justify-center text-graphite hover:text-near-black dark:hover:text-pure-white shadow-sm hover:shadow transition-all duration-200 z-10",
          collapsed && "rotate-180",
        )}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      {/* Logo */}
      <div className="h-[72px] flex items-center px-5 shrink-0">
        <div
          className={cn(
            "flex items-center w-full",
            collapsed && "justify-center",
          )}
        >
          <div
            className="text-[28px] font-bold tracking-tight text-near-black dark:text-pure-white"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            {collapsed ? (
              <span>
                M
                <span className="text-near-black dark:text-electric-lime">
                  .
                </span>
              </span>
            ) : (
              <motion.span
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
              >
                TWIIN
                <span className="text-near-black dark:text-electric-lime">
                  .
                </span>
              </motion.span>
            )}
          </div>
        </div>
      </div>

      {!collapsed && agents.length > 1 && (
        <div className="px-3 pb-3">
          <label className="block px-2 mb-1.5 text-[10px] font-semibold text-graphite dark:text-dark-muted uppercase tracking-wider">
            Active Agent
          </label>
          <select
            value={selectedAgentId ?? agents[0]?.id}
            onChange={(e) => setSelectedAgentId(e.target.value)}
            className="w-full h-10 rounded-buttons border border-stone/40 dark:border-dark-border bg-parchment-card/60 dark:bg-dark-ground px-3 text-sm font-medium text-near-black dark:text-dark-text outline-none focus:border-electric-lime"
          >
            {agents.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-3 py-2 overflow-y-auto custom-scrollbar pt-4">
        <div className="space-y-1.5">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "relative flex items-center gap-3.5 py-2.5 rounded-xl transition-all duration-200 group outline-none",
                  "text-graphite dark:text-dark-muted hover:text-near-black dark:hover:text-pure-white",
                  !isActive && "hover:bg-stone/30 dark:hover:bg-dark-surface/60",
                  isActive &&
                  "text-near-black dark:text-pure-white font-medium bg-stone/40 dark:bg-dark-surface shadow-[0_1px_2px_rgba(0,0,0,0.02)]",
                  collapsed ? "justify-center w-11 h-11 mx-auto px-0" : "px-3.5",
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className={cn(
                      "w-5 h-5 shrink-0 transition-transform duration-200",
                      !isActive && "group-hover:scale-110",
                      isActive && "text-near-black dark:text-pure-white",
                    )}
                  />
                  {!collapsed && (
                    <span className="text-[14.5px] truncate relative z-10 flex-1">
                      {item.label}
                    </span>
                  )}
                  {!collapsed && isActive && (
                    <div className="w-2 h-2 rounded-full bg-near-black dark:bg-pure-white shrink-0 shadow-sm" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Profile Section */}
      <div className="relative mt-auto p-3" ref={menuRef}>
        <AnimatePresence>
          {profileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute bottom-full left-3 mb-2 w-[236px] bg-pure-white dark:bg-dark-surface rounded-xl border border-stone dark:border-dark-border shadow-lg overflow-hidden origin-bottom-left"
              style={{ width: collapsed ? "180px" : "calc(100% - 24px)" }}
            >
              <div className="p-2 space-y-1">
                {/* Appearance toggle */}
                <div className="px-2 pt-1.5 pb-2">
                  <p className="text-[10px] font-semibold text-graphite dark:text-dark-muted uppercase tracking-wider mb-2">
                    Appearance
                  </p>
                  <div className="flex items-center bg-stone/30 dark:bg-dark-card rounded-badges p-1 gap-1">
                    <button
                      onClick={() => setTheme("light")}
                      className={cn(
                        "flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-badges text-xs font-medium transition-all duration-200",
                        theme === "light"
                          ? "bg-pure-white dark:bg-dark-surface text-near-black dark:text-pure-white shadow-sm"
                          : "text-graphite dark:text-dark-muted hover:text-near-black dark:hover:text-pure-white",
                      )}
                    >
                      <Sun className="w-3.5 h-3.5" />
                      Light
                    </button>
                    <button
                      onClick={() => setTheme("dark")}
                      className={cn(
                        "flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-badges text-xs font-medium transition-all duration-200",
                        theme === "dark"
                          ? "bg-pure-white dark:bg-dark-surface text-near-black dark:text-pure-white shadow-sm"
                          : "text-graphite dark:text-dark-muted hover:text-near-black dark:hover:text-pure-white",
                      )}
                    >
                      <Moon className="w-3.5 h-3.5" />
                      Dark
                    </button>
                  </div>
                </div>

                <div className="h-px bg-stone/50 dark:bg-dark-border" />

                {/* Settings */}
                <button
                  onClick={() => {
                    navigate("/dashboard/settings");
                    setProfileMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-near-black dark:text-dark-text hover:bg-stone/30 dark:hover:bg-dark-card rounded-badges transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  Settings
                </button>

                <div className="h-px bg-stone/50 dark:bg-dark-border" />

                {/* Sign out */}
                <button
                  onClick={() => {
                    signOut();
                    setProfileMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-coral-alert hover:bg-coral-alert/10 rounded-badges transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Sign out
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          onClick={() => setProfileMenuOpen(!profileMenuOpen)}
          className={cn(
            "flex items-center gap-3 p-2 rounded-buttons hover:bg-stone/30 dark:hover:bg-dark-surface/60 transition-colors outline-none",
            collapsed ? "w-[52px] justify-center mx-auto" : "w-full",
          )}
        >
          <div className="w-10 h-10 rounded-full bg-near-black dark:bg-dark-ground flex items-center justify-center shrink-0 shadow-sm border border-stone/50 dark:border-dark-border overflow-hidden">
            <img
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.fullName || user?.email || "User")}&background=beff50&color=14140f&bold=true&font-size=0.35`}
              alt="Avatar"
              className="w-full h-full object-cover"
            />
          </div>
          {!collapsed && user && (
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm font-medium text-near-black dark:text-pure-white truncate leading-tight">
                {user.fullName || user.email}
              </p>
              {user.companyName && (
                <p className="text-xs text-graphite dark:text-dark-muted truncate leading-tight mt-0.5">
                  {user.companyName}
                </p>
              )}
            </div>
          )}
        </button>
      </div>
    </div>
  );
}
